import {useCallback, useState} from "react";
import NotificationService from "@common/services/notification.service";
import {SelectedFileMeta} from "../models/types/convert-document-ai-modal.types";
import {clearConvertAiModalState, saveConvertAiModalState} from "../helpers/convert-ai-modal-state";

interface UseDocumentConversionParams {
    generateArticleFromDocument: (files: File[]) => Promise<unknown>;
    onSuccess: (draft: unknown) => void;
    deferUntilVisible: (action: () => void) => void;
}

interface UseDocumentConversionResult {
    isProcessing: boolean;
    convert: (files: File[], meta: SelectedFileMeta[]) => Promise<void>;
}

const buildSuccessMessage = (fileCount: number): string =>
    fileCount > 1
        ? "AI przygotowało treść artykułu na podstawie dokumentów."
        : "AI przygotowało treść artykułu na podstawie dokumentu.";

/**
 * Drives the document-to-article AI conversion.
 *
 * Tracks the processing flag, keeps it in sync with the persisted modal state,
 * and on success defers the notification and `onSuccess` callback until the tab
 * is visible — so a user who switched tabs sees the result when they return,
 * not while they are away.
 */
export const useDocumentConversion = (
    {generateArticleFromDocument, onSuccess, deferUntilVisible}: UseDocumentConversionParams
): UseDocumentConversionResult => {
    const [isProcessing, setIsProcessing] = useState(false);

    const convert = useCallback(async (files: File[], meta: SelectedFileMeta[]) => {
        const markProcessing = (processing: boolean) => {
            setIsProcessing(processing);
            saveConvertAiModalState({files: meta, isProcessing: processing});
        };

        markProcessing(true);

        try {
            const draft = await generateArticleFromDocument(files);
            markProcessing(false);

            deferUntilVisible(() => {
                NotificationService.success(buildSuccessMessage(files.length), "Konwersja AI");
                clearConvertAiModalState();
                onSuccess(draft);
            });
        } catch (error) {
            console.error(error);
            markProcessing(false);
        }
    }, [generateArticleFromDocument, onSuccess, deferUntilVisible]);

    return {isProcessing, convert};
};
